import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchEmergencyTools } from "../lib/api";
import { useCart } from "../context/CartContext";
import Stars from "../components/Stars";
import Seo from "../components/Seo";
import { ShoppingCart } from "lucide-react";

const TYPES = ["Jump Starters", "Air Compressors", "Kits"];
const BRANDS = ["NOCO", "AstroAI"];

const EmergencyTools = () => {
  const [tools, setTools] = useState([]);
  const [types, setTypes] = useState([...TYPES]);
  const [brands, setBrands] = useState([...BRANDS]);
  const [priceMax, setPriceMax] = useState(250);
  const [ratingMin, setRatingMin] = useState(0);
  const { addItem } = useCart();

  useEffect(() => {
    const params = {};
    if (types.length && types.length < TYPES.length) params.category = types.join(",");
    if (brands.length && brands.length < BRANDS.length) params.brand = brands.join(",");
    if (priceMax !== 250) params.price_max = priceMax;
    if (ratingMin > 0) params.rating_min = ratingMin;
    fetchEmergencyTools(params).then(setTools).catch(() => setTools([]));
  }, [types, brands, priceMax, ratingMin]);

  const toggle = (arr, v, setter) => setter(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  return (
    <div className="vd-page" data-testid="page-emergency-tools">
      <Seo
        title="Emergency Tools & Roadside Essentials"
        description="Jump starters, air compressors, safety kits & roadside emergency tools from NOCO, AstroAI and ValueDrive. Free UK delivery, expert support."
        path="/emergency-tools"
      />
      <section className="vd-section-secondary py-14">
        <div className="vd-container text-center">
          <h1 className="mb-3">Emergency Tools &amp; Roadside Essentials</h1>
          <p className="vd-muted max-w-2xl mx-auto">Be prepared for any situation on the road.</p>
        </div>
      </section>

      <section className="vd-container py-10 grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-8">
        <aside className="vd-card p-5 h-fit" data-testid="emergency-filters">
          <FilterGroup title="Filter by Type">
            {TYPES.map((t) => (
              <label key={t} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="checkbox" className="vd-checkbox" checked={types.includes(t)} onChange={() => toggle(types, t, setTypes)} data-testid={`filter-type-${t.toLowerCase().replace(/\s+/g, "-")}`} />
                <span className="vd-body text-[15px]">{t}</span>
              </label>
            ))}
          </FilterGroup>

          <FilterGroup title="Filter by Brand">
            {BRANDS.map((b) => (
              <label key={b} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="checkbox" className="vd-checkbox" checked={brands.includes(b)} onChange={() => toggle(brands, b, setBrands)} data-testid={`filter-tool-brand-${b.toLowerCase()}`} />
                <span className="vd-body text-[15px]">{b}</span>
              </label>
            ))}
          </FilterGroup>

          <FilterGroup title="Filter by Price">
            <div className="px-1">
              <input type="range" min="20" max="250" step="10" value={priceMax} onChange={(e) => setPriceMax(Number(e.target.value))} className="vd-range" data-testid="filter-tool-price" />
              <div className="flex justify-between vd-muted text-sm mt-1">
                <span>£20</span>
                <span>£{priceMax}</span>
              </div>
            </div>
          </FilterGroup>

          <FilterGroup title="Filter by Rating">
            {[4, 3].map((r) => (
              <label key={r} className="flex items-center gap-3 py-1.5 cursor-pointer">
                <input type="radio" name="tool-rating" className="vd-checkbox" checked={ratingMin === r} onChange={() => setRatingMin(r)} data-testid={`filter-tool-rating-${r}`} />
                <Stars value={r} size={14} />
                <span className="vd-body text-sm">{r} Stars &amp; Up</span>
              </label>
            ))}
            <label className="flex items-center gap-3 py-1.5 cursor-pointer">
              <input type="radio" name="tool-rating" className="vd-checkbox" checked={ratingMin === 0} onChange={() => setRatingMin(0)} data-testid="filter-tool-rating-all" />
              <span className="vd-body text-sm">All ratings</span>
            </label>
          </FilterGroup>
        </aside>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="emergency-tools-grid">
          {tools.length === 0 ? (
            <div className="col-span-full vd-card p-10 text-center"><p className="vd-muted">No tools match your filters.</p></div>
          ) : tools.map((t) => (
            <article key={t.id} className="vd-card overflow-hidden flex flex-col" data-testid={`tool-card-${t.id}`}>
              <Link to={`/emergency-tools/${t.id}`} className="block">
                <div className="aspect-[4/3] overflow-hidden" style={{ background: "var(--bg-secondary)" }}>
                  <img src={t.image} alt={t.name} className="w-full h-full object-cover transition-transform duration-300 hover:scale-105" />
                </div>
              </Link>
              <div className="p-5 flex flex-col flex-1">
                <span className="vd-muted text-xs uppercase tracking-wider">{t.brand}</span>
                <Link to={`/emergency-tools/${t.id}`} className="vd-heading font-semibold mt-1 hover:opacity-80" style={{ fontSize: 16 }}>{t.name}</Link>
                {t.spec_subtitle && <p className="vd-muted text-sm mt-1">{t.spec_subtitle}</p>}
                <p className="font-extrabold mt-3" style={{ color: "var(--primary-red)", fontSize: 22 }}>£{t.price.toFixed(2)}</p>
                <div className="mt-2"><Stars value={t.rating} size={14} /></div>
                <div className="flex flex-col gap-2 mt-4">
                  <button
                    onClick={() => addItem({ tyre_id: `tool-${t.id}`, name: t.name, price: t.price, image: t.image })}
                    className="vd-btn-primary justify-center"
                    data-testid={`add-tool-${t.id}`}
                  ><ShoppingCart size={16} /> Add to Basket</button>
                  <Link to={`/emergency-tools/${t.id}`} className="vd-btn-secondary justify-center" data-testid={`view-tool-${t.id}`}>View Details</Link>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

const FilterGroup = ({ title, children }) => (
  <div className="mb-6">
    <h4 className="vd-heading text-sm uppercase tracking-wider mb-3" style={{ letterSpacing: "0.08em" }}>{title}</h4>
    {children}
  </div>
);

export default EmergencyTools;
